<template>
  <div v-if="isFetched">
    <v-row align="center" class="pb-3" no-gutters>
      <v-col cols="auto" class="font-weight-medium pr-3">
        {{ $i18n.t('admin.publicMapTokens') }}
      </v-col>
      <v-col cols="auto">
        <base-rounded-button
          icon="mdi-plus"
          size="x-small"
          color="success"
          :tooltip="$i18n.t('button.addNewToken')"
          @click="openDialog('MainInput')"
        />
      </v-col>
    </v-row>
    <v-list class="py-0">
      <v-list-item v-for="token in publicTokens" class="border" :key="token.token">
        <v-list-item-content class="pa-0">
          <v-list-item-title class="d-flex align-center">
            <v-subheader class="text-body-2 font--text pl-0">
              {{ token.name }}
            </v-subheader>
            <v-tooltip color="font" bottom>
              <template #activator="{ on }">
                <v-chip v-on="on">
                  <span class="primary--text d-inline-block text-truncate">
                    {{ token.token }}
                  </span>
                  <v-icon style="cursor: pointer" color="primary" right small @click="copyToken(token.token)">
                    mdi-content-copy
                  </v-icon>
                </v-chip>
              </template>
              {{ token.token }}
            </v-tooltip>
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <dots-menu :items="dotsItems" @delete="deleteToken(token)" />
        </v-list-item-action>
      </v-list-item>
    </v-list>
    <main-input-dialog
      v-if="isMainInputDialogLoaded"
      :is-visible.sync="isMainInputDialogVisible"
      :inputs="inputs"
      :title-translation="$i18n.t('dialog.addToken')"
      agree-text="dialog.agreeAdd"
      :loading="isLoading"
      @agree="addPublicTokenHandler"
    >
      <template #modules.input="data">
        <v-col cols="7">
          <data-input v-model="selectedModules" v-bind="data">
            <template #prepend-item>
              <v-list-item @click="toggleSelectAll">
                <v-list-item-action>
                  <v-icon :color="selectedModules.length > 0 ? 'primary' : ''">
                    {{ iconSelectAll }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ $i18n.t('button.selectAllItems') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider />
            </template>
            <template #selection="{ item, index }">
              <v-chip v-if="index < 4" close @click:close="selectedModules.splice(index, 1)">
                <span class="d-inline-block text-truncate">{{ item.text }}</span>
              </v-chip>
              <span v-if="index === 4" class="text-caption grey--text">
                {{ `(+${$i18n.tc('admin.other', selectedModules.length - 4)})` }}
              </span>
            </template>
          </data-input>
        </v-col>
      </template>
    </main-input-dialog>
    <main-dialog
      v-if="isMainDialogLoaded"
      :is-visible.sync="isMainDialogVisible"
      :title="$i18n.t('dialog.title.deletePublicToken')"
      :body="
        $i18n.t('dialog.body.deletePublicTokenConfirmation', {
          name: selectedToken.name,
        })
      "
      agree-text="dialog.agreeDelete"
      :loading="isLoading"
      @agree="deletePublicTokenHandler"
    />
  </div>
  <main-loader v-else />
</template>

<script>
import { call, get } from 'vuex-pathify';

import DotsMenu from '@/components/DotsMenu';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'PublicTokens',
  components: {
    DotsMenu,
    DataInput,
    MainLoader,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  data: () => ({
    isMainInputDialogLoaded: false,
    isMainInputDialogVisible: false,
    isMainDialogLoaded: false,
    isMainDialogVisible: false,
    isLoading: false,
    isFetched: false,
    selectedModules: [],
    selectedToken: null,
    dotsItems: [{ name: 'delete' }],
  }),
  computed: {
    publicTokens: get('admin/publicTokens'),
    isModuleEnabled: get('admin/additionalModules@PUBLIC_TOKENS.enabled'),
    additionalModules: get('admin/additionalModules'),
    additionalModulesArray() {
      return this.$_objectToArray(this.additionalModules)
        .filter(module => module?.name !== 'PUBLIC_TOKENS')
        .map(el => {
          return {
            text: el?.verbose_name || '',
            value: el?.name || '',
          };
        });
    },
    iconSelectAll() {
      if (this.selectedModules.length === this.additionalModulesArray.length) return 'mdi-checkbox-marked';
      else if (this.selectedModules.length) return 'mdi-minus-box';
      else return 'mdi-checkbox-blank-outline';
    },
    inputs() {
      return [
        {
          name: 'name',
          translationPath: 'dialog.body.tokenName',
          dataType: { name: 'text' },
          rules: [v => !!v || v == '0' || 'rules.required'],
          cols: '12',
          leftCol: 4,
          rightCol: 7,
          spacer: true,
          value: '',
        },
        {
          name: 'modules',
          translationPath: 'dialog.body.availableModules',
          dataType: { name: 'select' },
          items: this.additionalModulesArray,
          rules: [v => v.length > 0 || 'rules.required'],
          leftCol: 4,
          spacer: true,
          chips: true,
          multiple: true,
          autoConvertSelect: false,
          align: 'start',
        },
      ];
    },
  },
  watch: {
    isModuleEnabled: {
      handler(nV) {
        if (nV) this.init();
        else this.isFetched = false;
      },
      immediate: true,
    },
  },
  methods: {
    getPublicTokens: call('admin/getPublicTokens'),
    addPublicToken: call('admin/addPublicToken'),
    deletePublicToken: call('admin/deletePublicToken'),
    openDialog(type) {
      this[`is${type}DialogLoaded`] = true;
      this[`is${type}DialogVisible`] = true;
    },
    copyToken(token) {
      navigator.clipboard.writeText(token);
    },
    toggleSelectAll() {
      if (this.selectedModules.length === this.additionalModulesArray.length) this.selectedModules = [];
      else this.selectedModules = this.additionalModulesArray.map(module => module.value);
    },
    deleteToken(token) {
      this.selectedToken = token;
      this.openDialog('Main');
    },
    async addPublicTokenHandler() {
      this.isLoading = true;
      try {
        await this.addPublicToken({
          name: this.inputs[0].value,
          modules: this.selectedModules,
        });
      } finally {
        this.isLoading = false;
        this.isMainInputDialogLoaded = false;
        this.isMainInputDialogVisible = false;
        this.inputs[0].value = '';
        this.selectedModules = [];
      }
    },
    async deletePublicTokenHandler() {
      this.isLoading = true;
      try {
        await this.deletePublicToken(this.selectedToken.token);
      } finally {
        this.selectedToken = null;
        this.isLoading = false;
        this.isMainDialogLoaded = false;
        this.isMainDialogVisible = false;
      }
    },
    async init() {
      await this.getPublicTokens();
      this.isFetched = true;
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-select .v-chip {
    margin: 2px;
  }
  .v-select__selections {
    flex-wrap: wrap;
  }
}
.border {
  @include border;
  &:not(:last-child) {
    border-bottom: none;
  }
}
</style>
